import React from "react";
import { CardBack } from "./CardBack";

const SUIT_SYMBOL = {
  spades: "♠",
  hearts: "♥",
  diamonds: "♦",
  clubs: "♣",
};

const SUIT_LETTER = { spades: "S", hearts: "H", diamonds: "D", clubs: "C" };

// ── Number cards (A–10) drawn like real ones: as many suit signs as the rank,
// laid out as on a deck of cards, those of the lower half upside down, and the
// index (rank over suit) in the top-left corner and, turned over, the
// bottom-right one. One SVG in a 100 × 145 box, so every size keeps the same
// drawing. ──

// The suits as on the splash screen (public/splash.js), in a 100 × 100 box:
// paths, never text, which a phone may draw as an emoji.
const SUIT_PATH = {
  spades: "M50 4C62 22 96 40 96 62C96 76 85 84 74 84C64 84 57 79 53 72C54 82 58 90 66 96H34C42 90 46 82 47 72C43 79 36 84 26 84C15 84 4 76 4 62C4 40 38 22 50 4Z",
  hearts: "M50 92C20 66 4 48 4 29C4 14 15 5 28 5C38 5 46 11 50 20C54 11 62 5 72 5C85 5 96 14 96 29C96 48 80 66 50 92Z",
  clubs: "M50 8A19 19 0 0 1 66 37A19 19 0 1 1 57 70C58 81 62 89 68 96H32C38 89 42 81 43 70A19 19 0 1 1 34 37A19 19 0 0 1 50 8Z",
  diamonds: "M50 2L88 50L50 98L12 50Z",
};
const SUIT_FILL = { spades: "#0f172a", clubs: "#0f172a", hearts: "#dc2626", diamonds: "#dc2626" };

// Where each rank's signs sit: [column -1 left / 0 middle / 1 right, row 0 top … 1 bottom].
const PIPS = {
  A: [[0, 0.5]],
  2: [[0, 0], [0, 1]],
  3: [[0, 0], [0, 0.5], [0, 1]],
  4: [[-1, 0], [1, 0], [-1, 1], [1, 1]],
  5: [[-1, 0], [1, 0], [0, 0.5], [-1, 1], [1, 1]],
  6: [[-1, 0], [1, 0], [-1, 0.5], [1, 0.5], [-1, 1], [1, 1]],
  7: [[-1, 0], [1, 0], [0, 0.25], [-1, 0.5], [1, 0.5], [-1, 1], [1, 1]],
  8: [[-1, 0], [1, 0], [0, 0.25], [-1, 0.5], [1, 0.5], [0, 0.75], [-1, 1], [1, 1]],
  9: [[-1, 0], [1, 0], [-1, 1 / 3], [1, 1 / 3], [0, 0.5], [-1, 2 / 3], [1, 2 / 3], [-1, 1], [1, 1]],
  10: [[-1, 0], [1, 0], [0, 1 / 6], [-1, 1 / 3], [1, 1 / 3], [-1, 2 / 3], [1, 2 / 3], [0, 5 / 6], [-1, 1], [1, 1]],
};

// The index's height per card size (in the 100-wide box), so it reads about as
// before: ~9 px on the table's cards, 12–14 px in the hand. The table's cards (xs)
// are only covered on their right (.card-fan): one corner, the rank alone, and the signs under it.
const FACE = {
  xs: { index: 30, corners: 1 },
  "xs-lg": { index: 30, corners: 1 },
  sm: { index: 25, corners: 2 },
  "sm-5": { index: 25, corners: 2 },
  md: { index: 23, corners: 2 },
  "md-5": { index: 23, corners: 2 },
  lg: { index: 20, corners: 2 },
};

function Sign({ d, fill, x, y, s, flip }) {
  return (
    <path d={d} fill={fill}
      transform={`translate(${x} ${y})${flip ? " rotate(180)" : ""} scale(${s / 100}) translate(-50 -50)`} />
  );
}

// The top-left index: the rank, and its sign under it. The index's column is R
// wide, enough for "10": the same for every rank.
function Corner({ rank, suit, size, sign }) {
  const { index: R } = FACE[size] ?? FACE.md;
  const fill = SUIT_FILL[suit] ?? "#0f172a";
  const ix = 4 + R / 2;
  return (
    <g>
      <text x={ix} y={3 + R * 0.8} fontSize={R} fontWeight="700" textAnchor="middle" fill={fill}
        letterSpacing={rank === "10" ? -R * 0.09 : 0} style={{ fontFamily: "inherit" }}>
        {rank}
      </text>
      {sign && <Sign d={SUIT_PATH[suit]} fill={fill} x={ix} y={8 + R * 1.2} s={R * 0.85} />}
    </g>
  );
}

// J/Q/K: the number cards' index, as large, on a white tab over the picture's
// corner. Opaque: the picture's own index, smaller, would show through.
function FaceIndex({ rank, suit, size }) {
  const { index: R } = FACE[size] ?? FACE.md;
  return (
    <svg viewBox="0 0 100 145" preserveAspectRatio="xMidYMid meet" aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none">
      {/* drawn from outside the box, so only its bottom-right corner is rounded */}
      <rect x={-10} y={-10} width={R + 18} height={R * 1.625 + 21} rx={8} fill="#fff" />
      <Corner rank={rank} suit={suit} size={size} sign />
    </svg>
  );
}

function NumberFace({ rank, suit, size }) {
  const { index: R, corners } = FACE[size] ?? FACE.md;
  const d = SUIT_PATH[suit];
  const fill = SUIT_FILL[suit] ?? "#0f172a";
  const col = R; // the index's column, as in Corner
  const corner = <Corner rank={rank} suit={suit} size={size} sign={corners === 2} />;

  // The room the signs share: between the two corners' columns (a gap after the
  // index, so « 4 » and its signs do not read as one block), or under the single index.
  const box = corners === 2
    ? { x0: 4 + col + 5, x1: 96 - col - 5, y0: 12, y1: 133 }
    : { x0: 8, x1: 92, y0: 6 + R * 0.95, y1: 140 };
  const pips = PIPS[rank] ?? PIPS.A;
  const ace = pips.length === 1;
  const w = box.x1 - box.x0;
  const s = ace ? Math.min(w * 0.9, 46) : Math.min(w * 0.42, (box.y1 - box.y0) / 4.4);
  const mid = (box.x0 + box.x1) / 2;
  const spread = Math.min(w / 2 - s / 2, s * 1.25);
  const top = box.y0 + s / 2;
  const bottom = box.y1 - s / 2;

  return (
    <svg viewBox="0 0 100 145" preserveAspectRatio="xMidYMid meet" aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none">
      {corner}
      {corners === 2 && <g transform="rotate(180 50 72.5)">{corner}</g>}
      {pips.map(([c, r], i) => (
        <Sign key={i} d={d} fill={fill} s={s}
          x={mid + c * spread}
          y={ace ? (box.y0 + box.y1) / 2 : top + r * (bottom - top)}
          flip={r > 0.5} />
      ))}
    </svg>
  );
}

// Preload face card images once at module load to avoid blank flash
const FACE_RANKS = ["J", "Q", "K"];
const SUIT_LETTERS = ["S", "H", "D", "C"];
if (typeof window !== "undefined") {
  FACE_RANKS.forEach((r) =>
    SUIT_LETTERS.forEach((s) => {
      const img = new Image();
      img.src = `/cards/${r}${s}.webp`;
    })
  );
  const joker = new Image();
  joker.src = "/joker.webp";
}

const SIZES = {
  xs:      "w-8 h-12 text-[9px]",
  // The table's cards on a computer's large screen (lib/desktop.js): the xs drawing, from 48 × 72 up to
  // 64 × 96 as the screen grows.
  "xs-lg": "w-[clamp(48px,min(3.3vw,6vh),64px)] h-[clamp(72px,min(4.95vw,9vh),96px)] text-[11px]",
  sm:      "w-11 h-16 text-[11px]",
  // sm/md réduits de 5% — utilisés dans PiocheArea uniquement
  "sm-5":  "w-[42px] h-[61px] text-[10px]",
  "md-5":  "w-[53px] h-[76px] text-[11px] sm:w-[61px] sm:h-[91px] sm:text-[12px]",
  md:      "w-14 h-20 text-[12px] sm:w-16 sm:h-24 sm:text-sm",
  lg:      "w-20 h-32 text-base",
};

const ROUNDED = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded",
  lg: "rounded-lg",
};

export const PlayingCard = React.memo(function PlayingCard({
  card,
  selected = false,
  onClick,
  faceDown = false,
  size = "md",
  rounded = "lg",
  testId,
  layoutId: _layoutId, // kept for API compat, unused here
  disabled = false,
  readOnly = false, // with `disabled`: a card to read (the round's recap), not a control turned off: no dimming
}) {
  const sizeClass = SIZES[size] ?? SIZES.md;
  const roundedClass = ROUNDED[rounded] ?? "rounded-lg";

  // CSS-driven lift: selected → always lifted; not-selected → lift on hover.
  // Using will-change-transform + GPU-composited translate for 60fps perf.
  const liftClass = disabled
    ? ""
    : selected
    ? "-translate-y-[22px]"
    : "hover:-translate-y-[10px] hover:scale-[1.04]";

  const baseClass = `${sizeClass} ${roundedClass} card-shadow border-2 select-none transition-transform duration-100 ease-out will-change-transform`;

  if (faceDown) {
    return (
      <div className={`${sizeClass} ${roundedClass} card-shadow card-back relative overflow-hidden`} data-testid={testId}>
        <CardBack />
      </div>
    );
  }

  const isJoker = card?.rank === "JOKER" || !card?.suit;
  const rank = isJoker ? "JOKER" : card?.rank;
  const suit = card?.suit;
  const isFace = !isJoker && FACE_RANKS.includes(rank);
  const symbol = isJoker ? "★" : SUIT_SYMBOL[suit];

  const selectedBorder = selected ? "selected-card border-red-500" : "border-slate-200";
  const cursorClass = readOnly ? "" : disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer";

  // ---------- JOKER ----------
  if (isJoker) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        className={`${baseClass} block bg-white relative overflow-hidden font-body ${selectedBorder} ${cursorClass} ${liftClass}`}
        data-testid={testId}
      >
        <img
          src="/joker.webp"
          alt="Joker"
          draggable={false}
          decoding="async"
          style={{ transform: "scale(1.18)", transformOrigin: "center" }}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
        <div className="absolute top-0 left-0 px-1 py-0.5 text-[8px] sm:text-[9px] font-bold text-fuchsia-700 bg-white/70 rounded-br">
          JKR
        </div>
      </button>
    );
  }

  // ---------- J / Q / K ----------
  if (isFace) {
    const imgSrc = `/cards/${rank}${SUIT_LETTER[suit]}.webp`;
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        className={`${baseClass} block bg-white relative overflow-hidden font-body ${selectedBorder} ${cursorClass} ${liftClass}`}
        data-testid={testId}
      >
        <img
          src={imgSrc}
          alt={`${rank}${suit}`}
          draggable={false}
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
        <FaceIndex rank={rank} suit={suit} size={size} />
      </button>
    );
  }

  // ---------- Number cards (A, 2–10), every size ----------
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={`${baseClass} block bg-white relative overflow-hidden font-body ${selectedBorder} ${cursorClass} ${liftClass}`}
      data-testid={testId}
      aria-label={`${rank} ${symbol}`}
    >
      <NumberFace rank={rank} suit={suit} size={size} />
    </button>
  );
});

export default PlayingCard;
